// Group messaging via sender keys. Each member owns a SenderKey (a forward-ratcheting
// chain key + an Ed25519 signing key) and distributes it to the others over the pairwise
// Double Ratchet channels. On any membership change every member rotates to a new epoch,
// so a removed member can't read future messages and an added member can't read the past.
import { canonicalize, utf8, toB64, fromB64 } from "@shared/encoding.js";
import { CTX, KEY_BYTES, MAX_SKIP } from "@shared/constants.js";
import { type KeyPair, randomBytes, genEd25519, kdf, sign, verify, aeadSeal, aeadOpen, wipe } from "./sodium.js";
import { pad, unpad } from "@shared/padding.js";
import type { GroupHeader } from "@shared/wire.js";

/** Our own sending state for a group epoch. */
export interface SenderKey {
  epoch: number;
  chainKey: Uint8Array;
  index: number;
  sig: KeyPair; // Ed25519
}

/** A peer's sending state as we track it for decryption. */
export interface ReceiverKey {
  epoch: number;
  chainKey: Uint8Array;
  index: number;
  sigPub: Uint8Array;
  skipped: Map<number, Uint8Array>;
}

/** What gets pairwise-encrypted and shipped to each member. */
export interface SenderKeyDistribution {
  epoch: number;
  chainKey: string;
  index: number;
  sigPub: string;
}

export const createSenderKey = (epoch: number): SenderKey => ({
  epoch,
  chainKey: randomBytes(KEY_BYTES),
  index: 0,
  sig: genEd25519(),
});

function step(chainKey: Uint8Array): { next: Uint8Array; mk: Uint8Array } {
  return { next: kdf(chainKey, CTX.groupChain + "/chain"), mk: kdf(chainKey, CTX.groupChain + "/msg") };
}

const groupAad = (epoch: number, index: number): Uint8Array => utf8(canonicalize({ epoch, index }));

export function groupEncrypt(sender: SenderKey, plaintext: Uint8Array): GroupHeader & { ct: string } {
  const { next, mk } = step(sender.chainKey);
  const index = sender.index;
  sender.chainKey = next;
  sender.index++;
  const { nonce, ct } = aeadSeal(mk, pad(plaintext), groupAad(sender.epoch, index));
  wipe(mk);
  const sig = sign(ct, sender.sig.priv);
  return { t: "group", epoch: sender.epoch, index, nonce: toB64(nonce), sig: toB64(sig), ct: toB64(ct) };
}

export function groupDecrypt(recv: ReceiverKey, header: GroupHeader, ctB64: string): Uint8Array {
  if (header.epoch !== recv.epoch) throw new Error("sender-key epoch mismatch (rotation not yet applied)");
  const ct = fromB64(ctB64);
  if (!verify(fromB64(header.sig), ct, recv.sigPub)) throw new Error("invalid group message signature");

  const open = (mk: Uint8Array): Uint8Array => {
    const plain = unpad(aeadOpen(mk, fromB64(header.nonce), ct, groupAad(header.epoch, header.index)));
    wipe(mk);
    return plain;
  };

  const cached = recv.skipped.get(header.index);
  if (cached) {
    recv.skipped.delete(header.index);
    return open(cached);
  }
  if (header.index < recv.index) throw new Error("group message key already consumed and not cached");
  if (header.index - recv.index > MAX_SKIP) throw new Error("too many skipped group messages");

  while (recv.index < header.index) {
    const { next, mk } = step(recv.chainKey);
    recv.chainKey = next;
    recv.skipped.set(recv.index, mk);
    recv.index++;
  }
  const { next, mk } = step(recv.chainKey);
  recv.chainKey = next;
  recv.index++;
  return open(mk);
}

// ---- Distribution & rotation ----------------------------------------------

export const exportSenderKey = (s: SenderKey): SenderKeyDistribution => ({
  epoch: s.epoch,
  chainKey: toB64(s.chainKey),
  index: s.index,
  sigPub: toB64(s.sig.pub),
});

export const importSenderKey = (d: SenderKeyDistribution): ReceiverKey => ({
  epoch: d.epoch,
  chainKey: fromB64(d.chainKey),
  index: d.index,
  sigPub: fromB64(d.sigPub),
  skipped: new Map(),
});

/** Rotate to the next epoch on add/remove. The caller redistributes to the new member set. */
export const rotateSenderKey = (current: SenderKey): SenderKey => createSenderKey(current.epoch + 1);

// ---- Serialization for the local store ------------------------------------

export interface SerializedSender {
  epoch: number;
  chainKey: string;
  index: number;
  sig_pub: string;
  sig_priv: string;
}
export const serializeSender = (s: SenderKey): SerializedSender => ({
  epoch: s.epoch,
  chainKey: toB64(s.chainKey),
  index: s.index,
  sig_pub: toB64(s.sig.pub),
  sig_priv: toB64(s.sig.priv),
});
export const deserializeSender = (s: SerializedSender): SenderKey => ({
  epoch: s.epoch,
  chainKey: fromB64(s.chainKey),
  index: s.index,
  sig: { pub: fromB64(s.sig_pub), priv: fromB64(s.sig_priv) },
});

export interface SerializedReceiver {
  epoch: number;
  chainKey: string;
  index: number;
  sig_pub: string;
  skipped: [number, string][];
}
export const serializeReceiver = (r: ReceiverKey): SerializedReceiver => ({
  epoch: r.epoch,
  chainKey: toB64(r.chainKey),
  index: r.index,
  sig_pub: toB64(r.sigPub),
  skipped: [...r.skipped.entries()].map(([k, v]) => [k, toB64(v)]),
});
export const deserializeReceiver = (r: SerializedReceiver): ReceiverKey => ({
  epoch: r.epoch,
  chainKey: fromB64(r.chainKey),
  index: r.index,
  sigPub: fromB64(r.sig_pub),
  skipped: new Map(r.skipped.map(([k, v]) => [k, fromB64(v)])),
});
